if (!customElements.get('service-body-hero')) {
  class ServiceBodyHero extends HTMLElement {
    connectedCallback() {
      if (this.controller && !this.controller.signal.aborted) return;

      this.stage = this.querySelector('[data-stage]');
      this.controls = this.querySelector('[data-angle-controls]');
      this.panels = [...this.querySelectorAll('[data-angle-panel]')];
      this.buttons = [...this.querySelectorAll('[data-angle]')];
      this.motionToggle = this.querySelector('[data-motion-toggle]');
      if (!this.stage || !this.controls || !this.panels.length || !this.buttons.length || !this.motionToggle) return;

      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.motionRequested ??= this.dataset.motion === 'playing';
      this.dataset.inView = 'true';

      this.selectAngle(this.buttons.find((button) => button.dataset.angle === this.dataset.activeAngle) || this.buttons[0]);
      this.controls.hidden = false;
      this.motionToggle.hidden = false;
      this.updateMotion();

      this.controls.addEventListener('click', (event) => {
        const button = event.target.closest('[data-angle]');
        if (this.buttons.includes(button)) this.selectAngle(button);
      }, options);

      this.controls.addEventListener('keydown', (event) => {
        if (event.altKey || event.ctrlKey || event.metaKey) return;
        const button = event.target.closest('[data-angle]');
        const current = this.buttons.indexOf(button);
        if (current < 0) return;
        let next;
        if (event.key === 'ArrowRight') next = (current + 1) % this.buttons.length;
        if (event.key === 'ArrowLeft') next = (current - 1 + this.buttons.length) % this.buttons.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.buttons.length - 1;
        if (next === undefined) return;
        event.preventDefault();
        this.selectAngle(this.buttons[next]);
        this.buttons[next].focus();
      }, options);

      this.motionToggle.addEventListener('click', () => {
        if (this.reducedMotion.matches) return;
        this.motionRequested = !this.motionRequested;
        this.updateMotion();
      }, options);

      this.stage.addEventListener('pointermove', (event) => {
        if (!this.canTilt() || event.pointerType === 'touch') return;
        this.pointer = { x: event.clientX, y: event.clientY };
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          if (!this.canTilt()) return;
          const bounds = this.stage.getBoundingClientRect();
          if (!bounds.width || !bounds.height) return;
          const clamp = (value) => Math.max(-1, Math.min(1, value));
          const x = clamp((this.pointer.x - bounds.left) / bounds.width * 2 - 1);
          const y = clamp((this.pointer.y - bounds.top) / bounds.height * 2 - 1);
          this.style.setProperty('--sb-tilt-x', `${(-y * 4).toFixed(2)}deg`);
          this.style.setProperty('--sb-tilt-y', `${(x * 4).toFixed(2)}deg`);
          this.style.setProperty('--sb-shift-x', `${(x * 10).toFixed(2)}px`);
          this.style.setProperty('--sb-shift-y', `${(y * 10).toFixed(2)}px`);
        });
      }, options);

      this.stage.addEventListener('pointerleave', () => this.resetTilt(), options);
      this.stage.addEventListener('pointercancel', () => this.resetTilt(), options);
      this.reducedMotion.addEventListener('change', () => this.updateMotion(), options);
      this.finePointer.addEventListener('change', () => this.resetTilt(), options);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          const entry = entries.find((item) => item.target === this);
          if (!entry) return;
          this.dataset.inView = String(entry.isIntersecting);
          if (!entry.isIntersecting) this.resetTilt();
        });
        this.observer.observe(this);
      }
    }

    selectAngle(button) {
      const index = Number(button.dataset.angle);
      if (!Number.isInteger(index) || !this.panels[index]) return;
      this.panels.forEach((panel, position) => { panel.hidden = position !== index; });
      this.buttons.forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
      this.dataset.activeAngle = String(index);
      const status = this.querySelector('[data-angle-status]');
      const count = this.querySelector('[data-angle-count]');
      if (status) status.textContent = button.dataset.angleLabel || button.textContent.trim();
      if (count) count.textContent = String(index + 1).padStart(2, '0');
    }

    updateMotion() {
      const reduced = this.reducedMotion.matches;
      const playing = this.motionRequested && !reduced;
      this.dataset.motion = playing ? 'playing' : 'paused';
      this.motionToggle.disabled = reduced;
      const label = reduced ? 'reducedLabel' : playing ? 'pauseLabel' : 'playLabel';
      if (this.motionToggle.dataset[label]) this.motionToggle.textContent = this.motionToggle.dataset[label];
      if (!playing) this.resetTilt();
    }

    canTilt() {
      return this.dataset.motion === 'playing' && this.dataset.inView !== 'false' && !this.reducedMotion.matches && this.finePointer.matches;
    }

    resetTilt() {
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      this.style.setProperty('--sb-tilt-x', '0deg');
      this.style.setProperty('--sb-tilt-y', '0deg');
      this.style.setProperty('--sb-shift-x', '0px');
      this.style.setProperty('--sb-shift-y', '0px');
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.resetTilt();
    }
  }

  customElements.define('service-body-hero', ServiceBodyHero);
}
